import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, MapPin, CalendarCheck } from "lucide-react";
import { useLang } from "@/context";
import { CONTACT, waLink, SERVICE_IMAGES } from "@/i18n";
import { scrollToId } from "@/lib/scroll";

export const Hero = () => {
    const { t } = useLang();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
    const yMark = useTransform(scrollYProgress, [0, 1], [0, -70]);

    return (
        <section id="home" ref={ref} data-testid="hero-section" className="relative overflow-hidden hero-grain bg-[#FBF7F3]">
            <motion.img
                src="/logo-mark.png"
                style={{ y: yMark }}
                className="pointer-events-none select-none absolute -right-28 top-28 w-[560px] opacity-[0.05]"
                alt=""
                aria-hidden="true"
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center relative">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="inline-flex items-center gap-2 rounded-full border border-[#8D1717]/25 bg-white px-4 py-1.5 text-xs font-semibold text-[#8D1717] tracking-wide uppercase"
                        data-testid="hero-badge"
                    >
                        <span className="w-2 h-2 rounded-full bg-[#22C55E] animate-pulse" />
                        {t.hero.badge}
                    </motion.div>

                    <h1 className="font-display font-black tracking-tight leading-[1.02] text-[#2A0505] text-4xl sm:text-5xl lg:text-[4.2rem] mt-6">
                        {t.hero.titleLines.map((line, i) => (
                            <span key={line} className="block overflow-hidden pb-1.5 -mb-1.5">
                                <motion.span
                                    className={`block ${i === t.hero.titleLines.length - 1 ? "italic text-[#8D1717]" : ""}`}
                                    initial={{ y: "110%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 0.9, delay: 0.15 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.65 }}
                        className="text-base sm:text-lg text-[#6B5B57] leading-relaxed mt-6 max-w-xl"
                    >
                        {t.hero.subtitle}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.8 }}
                        className="flex flex-wrap items-center gap-3 mt-8"
                    >
                        <motion.a
                            whileTap={{ scale: 0.97 }}
                            href={`tel:${CONTACT.phone1}`}
                            data-testid="hero-call-now-button"
                            className="inline-flex items-center gap-2 bg-[#8D1717] hover:bg-[#6D1010] text-white font-semibold rounded-full px-7 py-4 text-base shadow-lg shadow-[#8D1717]/25 transition-colors"
                        >
                            <Phone className="w-5 h-5" />{t.cta.call}
                        </motion.a>
                        <motion.a
                            whileTap={{ scale: 0.97 }}
                            href={waLink()}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="hero-whatsapp-button"
                            className="inline-flex items-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold rounded-full px-7 py-4 text-base shadow-lg shadow-[#22C55E]/25 transition-colors"
                        >
                            <MessageCircle className="w-5 h-5" />{t.cta.whatsapp}
                        </motion.a>
                        <motion.button
                            whileTap={{ scale: 0.97 }}
                            onClick={() => scrollToId("book")}
                            data-testid="hero-book-service-button"
                            className="inline-flex items-center gap-2 border-2 border-[#2A0505]/15 hover:border-[#8D1717] hover:text-[#8D1717] text-[#2A0505] font-semibold rounded-full px-7 py-[14px] text-base transition-colors"
                        >
                            <CalendarCheck className="w-5 h-5" />{t.cta.book}
                        </motion.button>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.7, delay: 1 }}
                        className="flex items-center gap-2 text-sm text-[#8a756f] mt-7"
                        data-testid="hero-serving-line"
                    >
                        <MapPin className="w-4 h-4 text-[#8D1717]" />{t.hero.serving}
                    </motion.p>
                </div>

                <div className="lg:col-span-5 relative">
                    <motion.div
                        initial={{ opacity: 0, y: 48 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="relative"
                    >
                        <div className="relative rounded-t-[10rem] rounded-b-[2rem] overflow-hidden border-[6px] border-white shadow-2xl shadow-[#2A0505]/20 aspect-[4/5]">
                            <motion.img
                                src={SERVICE_IMAGES.hero}
                                style={{ y: yImg }}
                                className="w-full h-full object-cover scale-110"
                                alt="Technician performing home appliance repair at a doorstep in Kolkata"
                            />
                        </div>
                        <div className="float-slow absolute -left-4 sm:-left-8 top-10 backdrop-blur-xl bg-white/90 border border-[#EADFD8] rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3">
                            <span className="text-sm font-semibold text-[#2A0505]">{t.hero.card1}</span>
                        </div>
                        <div className="float-slow absolute -right-3 sm:-right-6 bottom-12 backdrop-blur-xl bg-white/90 border border-[#EADFD8] rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3" style={{ animationDelay: "1.4s" }}>
                            <span className="text-sm font-semibold text-[#2A0505]">{t.hero.card2}</span>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
